import type { ExtractInjectionAPI, PMPluginFactoryParams } from '@atlaskit/editor-common/types';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import type { AIPlugin } from '../../aiPluginType';

import { checkContentEligibility } from './content-eligibility';
import { hasTabConflict } from './context-conflict-detector';
import { createShowPromptCommand, getPluginState } from './plugin-factory';
import type { StandDownManager } from './stand-down-manager';

export const onIdle = ({
	api,
	getIntl,
	standDownManager,
	view,
}: {
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	getIntl: PMPluginFactoryParams['getIntl'];
	standDownManager: StandDownManager;
	view: EditorView;
}) => {
	// - Check if AI is active (running or streaming or showing accept/reject review)
	const aiState = api?.aiExperience?.sharedState.currentState();
	if (
		aiState?.aiDecorationExperiencePluginState?.isRunning ||
		aiState?.isStreaming ||
		aiState?.isShowingAcceptRejectScreen
	) {
		return;
	}

	// - Check stand-down
	if (standDownManager.shouldStandDown()) {
		return;
	}

	// - Check Tab conflict
	if (hasTabConflict(view.state.selection)) {
		return;
	}

	// - Check content eligibility
	const eligibleResult = checkContentEligibility({
		locale: getIntl().locale,
		selection: view.state.selection,
	});
	if (!eligibleResult) {
		return;
	}

	// - Check if the eligible text and range have not changed
	const pluginState = getPluginState(view.state);
	const lastEligibleResult = pluginState?.lastEligibleResult;
	if (
		lastEligibleResult?.range.from === eligibleResult.range.from &&
		lastEligibleResult?.range.to === eligibleResult.range.to &&
		lastEligibleResult?.text === eligibleResult.text
	) {
		return;
	}

	// - Show the prompt
	createShowPromptCommand(eligibleResult.range)(view.state, view.dispatch);
};
